<script setup lang="ts">
    import { inject, ref, computed, watch, onMounted } from 'vue';
    import DateTimePicker from '@shared/components/DateTimePicker/DateTimePicker.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import EventEditorDetailsNav from '@views/Event/EventEditor/Details/EventEditorDetailsNav.vue';
    import { getTimeZones, type TimeZone } from '@views/Account/AccountInfo.access';
    import { type Event } from '@views/Event/EventTypes';
    import { useDateTimeField } from '@views/Event/EventEditor/Details/use-date-time-field';
    import { eventKey, queueEventPropertyKey, saveEventPropertyKey, canEditKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;
    const queueEventProperty = inject(queueEventPropertyKey)!;

    const timeZones = ref<TimeZone[]>([]);
    onMounted(async () => {
        timeZones.value = await getTimeZones();
    });

    const {
        date: startDate,
        hour: startHour,
        minute: startMinute,
        value: startValue,
        isComplete: isStartComplete,
        isDateMissing: isStartDateMissing,
        isTimeMissing: isStartTimeMissing,
    } = useDateTimeField(() => event.value?.Start);
    const end = useDateTimeField(() => event.value?.End);
    const { date: endDate, hour: endHour, minute: endMinute, isDateMissing: isEndDateMissing, isTimeMissing: isEndTimeMissing } = end;
    const hasEnd = ref(false);
    const timeZone = ref('');

    watch(
        () => event.value?.End,
        (value) => (hasEnd.value = !!value),
        { immediate: true }
    );

    watch(
        () => event.value?.TimeZone,
        (value) => (timeZone.value = value ?? ''),
        { immediate: true }
    );

    // Without an end, the end side counts as complete and is saved as empty.
    const isEndComplete = computed(() => !hasEnd.value || end.isComplete.value);
    const endValue = computed(() => (hasEnd.value ? end.value.value : null));

    // ISO "yyyy-MM-ddTHH:mm:ss" strings compare correctly as plain strings
    const isRangeInvalid = computed(() => !!startValue.value && !!endValue.value && startValue.value > endValue.value);

    // Saves both sides once the range is valid, so a side held back while invalid is persisted when the other side fixes it
    function saveRange() {
        if (isRangeInvalid.value) return;
        // Queued even when equal to the saved value, so stepping back cancels a pending save of the value in between.
        if (isStartComplete.value) queueEventProperty('Start', startValue.value ?? '', $Resources.EventNavDetailsStartAndEnd);
        if (isEndComplete.value) queueEventProperty('End', endValue.value ?? '', $Resources.EventNavDetailsStartAndEnd);
    }

    // Defaults the end to one hour after start
    function setEndAfterStart() {
        if (!startDate.value) return;
        const [year, month, day] = startDate.value.split('-').map(Number);
        const next = new Date(year, month - 1, day, (startHour.value ?? 0) + 1, startMinute.value ?? 0);
        endDate.value = `${next.getFullYear()}-${(next.getMonth() + 1).toString().padStart(2, '0')}-${next.getDate().toString().padStart(2, '0')}`;
        endHour.value = next.getHours();
        endMinute.value = next.getMinutes();
    }

    function onHasEndChange(newValue: boolean) {
        hasEnd.value = newValue;
        if (newValue) {
            setEndAfterStart();
        } else {
            endDate.value = null;
            endHour.value = null;
            endMinute.value = null;
        }
        saveRange();
    }

    async function onTimeZoneChange(newValue: string) {
        timeZone.value = newValue;
        // Dates arrive from the API mapped to the event's time zone, so reload to show them in the new one (same as the old KO editor).
        // Only on success, so a failed save keeps its error alert visible.
        if (await saveEventProperty('TimeZone', newValue, $Resources.GenericTimeZone)) {
            window.location.reload();
        }
    }

    const timeZoneOptions = computed(() => timeZones.value.map((tz) => ({ value: tz.TimeZoneId, label: tz.Name })));
</script>

<template>
    <div v-if="event" class="max-900">
        <h2 class="h3 m-0-t">{{ $Resources.WhenIsEventLabel }}?</h2>

        <div class="row">
            <div class="col-md-4">
                <DateTimePicker
                    class="form-group"
                    type="date"
                    :class="{ 'has-error': isStartDateMissing }"
                    :label="$Resources.GenericStartDate"
                    v-model:value="startDate"
                    start-empty
                    clearable
                    :disabled="!canEdit"
                    @update:value="saveRange" />
            </div>
            <div class="col-md-4">
                <DateTimePicker
                    class="form-group"
                    type="time"
                    :class="{ 'has-error': isStartTimeMissing }"
                    :label="$Resources.GenericStartTime"
                    v-model:value_hour="startHour"
                    v-model:value_minute="startMinute"
                    clearable
                    :disabled="!canEdit"
                    @update:value_hour="saveRange"
                    @update:value_minute="saveRange" />
            </div>
            <div class="col-md-4">
                <FormSelect
                    class="form-group"
                    :label="$Resources.GenericTimeZone"
                    :modelValue="timeZone"
                    :options="timeZoneOptions"
                    :disabled="!canEdit"
                    @update:modelValue="onTimeZoneChange" />
            </div>
        </div>

        <div class="form-group">
            <FormCheckable
                :label="$Resources.EventHasEndDateAndTimeToggleLabel"
                :switch-style="SwitchStyle.Standard"
                :checked="hasEnd"
                :disabled="!canEdit"
                @update:modelValue="(newVal) => onHasEndChange(newVal as boolean)"></FormCheckable>
        </div>

        <div v-if="hasEnd" class="row">
            <div class="col-md-4">
                <DateTimePicker
                    class="form-group"
                    type="date"
                    :class="{ 'has-error': isEndDateMissing }"
                    :label="$Resources.GenericEndDate"
                    v-model:value="endDate"
                    start-empty
                    clearable
                    :disabled="!canEdit"
                    @update:value="saveRange" />
            </div>
            <div class="col-md-4">
                <DateTimePicker
                    class="form-group"
                    type="time"
                    :class="{ 'has-error': isEndTimeMissing }"
                    :label="$Resources.GenericEndTime"
                    v-model:value_hour="endHour"
                    v-model:value_minute="endMinute"
                    clearable
                    :disabled="!canEdit"
                    @update:value_hour="saveRange"
                    @update:value_minute="saveRange" />
            </div>
        </div>

        <BsAlert v-if="isRangeInvalid" :type="AlertTypes.Danger" :content="$Resources.StartAfterEndError" :dismissible="false" />

        <EventEditorDetailsNav v-if="event" :eventId="props.eventId" />
    </div>
</template>
